import React from 'react';
import styled from 'styled-components';
import { panel } from './panelTheme';
import { PropertiesPanelProperty, PropertiesPanelPropertyContent } from './propertiesPanelProperty';

// L'INTERRUTTORE DI UNA RIGA DI PROPRIETÀ. Non è la spunta: una spunta dice «questa voce è
// scelta» e sta in un elenco di voci; un interruttore accende e spegne una cosa che c'è già, e in
// una colonna di parametri è la forma giusta — «Cast shadows» non è una voce scelta fra altre, è
// una cosa accesa.
//
// LA RIGA È LARGA TUTTA. Le altre proprietà stanno su due colonne — nome a sinistra, controllo a
// destra — perché il controllo è largo. L'interruttore è largo trenta pixel: messo all'inizio
// della seconda colonna resterebbe in mezzo al vuoto, lontano tanto dal nome quanto dal bordo.
// Sta in fondo alla riga, come nel disegno.

const Row = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-height: 28px;
`;

const Text = styled.span`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

const Label = styled.span`
    font-size: 12px;
    color: ${panel.ink};
`;

const Caption = styled.span`
    font-size: 10.5px;
    color: ${panel.inkMuted};
`;

// LA PALLINA È ::before E NON ::after. Sembra un capriccio e non lo è: `::after` è quello che i
// tooltip di questo backoffice usano per la loro pastiglia, e un interruttore che portasse un
// tooltip si vedrebbe la pallina spostata dal `transform` del tooltip — cioè un interruttore
// senza pallina.
const Track = styled.button<{ $on: boolean }>`
    position: relative;
    flex: 0 0 auto;
    width: 30px;
    height: 17px;
    padding: 0;
    border-radius: 9px;
    border: 1px solid ${(props) => (props.$on ? panel.accent : panel.line)};
    background: ${(props) => (props.$on ? panel.accent : panel.field)};
    cursor: pointer;
    transition:
        background 130ms ease,
        border-color 130ms ease;

    &::before {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 11px;
        height: 11px;
        border-radius: 50%;
        background: ${(props) => (props.$on ? panel.accentInk : `color-mix(in srgb, ${panel.ink} 45%, transparent)`)};
        transform: translateX(${(props) => (props.$on ? '13px' : '0')});
        transition:
            transform 130ms ease,
            background 130ms ease;
    }

    &:focus-visible {
        outline: 2px solid ${panel.accent};
        outline-offset: 2px;
    }

    /* Perde il COLORE e non la consistenza: l'arancione di un interruttore acceso vuol dire
       «questo è attivo», e su una riga che non vale è la cosa da togliere. Sbiadirlo invece
       raddoppierebbe il velo che la riga mette già di suo. */
    &[disabled] {
        cursor: not-allowed;
        filter: grayscale(1);
    }
`;

export type PropertiesPanelSwitchProps = {
    id?: string;
    checked: boolean;
    /** Il nome, per chi non vede l'interruttore. Serve quando il nome non è scritto accanto. */
    ariaLabel?: string;
    /** L'etichetta scritta che lo nomina, quando c'è. */
    labelledBy?: string;
    onChange?: (checked: boolean) => void;
    disabled?: boolean;
};

// L'INTERRUTTORE DA SOLO, senza la riga intorno. Serve dove il soggetto non è un parametro ma un
// gruppo intero — l'interruttore nella testata di una card — e lì il nome ce l'ha già la testata:
// una seconda etichetta accanto sarebbe la stessa parola due volte.
export const PropertiesPanelSwitch: React.FC<PropertiesPanelSwitchProps> = ({ id, checked, ariaLabel, labelledBy, onChange, disabled }) => (
    <Track
        id={id}
        type='button'
        role='switch'
        aria-checked={checked}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        $on={checked}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
    />
);

export type PropertiesPanelSwitchPropertyProps = {
    id: string;
    label: React.ReactNode;
    checked: boolean;
    /** La riga di spiegazione sotto il nome, quando il nome da solo non basta. */
    caption?: React.ReactNode;
    onChange?: (checked: boolean) => void;
    disabled?: boolean;
};

export const PropertiesPanelSwitchProperty: React.FC<PropertiesPanelSwitchPropertyProps> = ({ id, label, checked, caption, onChange, disabled }) => {
    return (
        <PropertiesPanelProperty disabled={disabled}>
            <PropertiesPanelPropertyContent>
                <Row>
                    <Text>
                        <Label id={`${id}-label`}>{label}</Label>
                        {caption && <Caption>{caption}</Caption>}
                    </Text>
                    <PropertiesPanelSwitch id={id} checked={checked} labelledBy={`${id}-label`} disabled={disabled} onChange={onChange} />
                </Row>
            </PropertiesPanelPropertyContent>
        </PropertiesPanelProperty>
    );
};
